import React from 'react';
import { batch } from 'react-redux';
import {
  ICohortRow,
  IRowMeta,
  populateNamedBuilderCohort,
  populateNamedCohortReference,
  removeErrorAction,
  resetCohortRow,
  RowType,
  setRowPropAction,
} from '../../../../../redux/slices/oldBuilder';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  activeSchemaIdSelector,
  rowSelector,
  rowsSelector,
} from '../../../../../redux/selectors/builder';
import {
  PermissionKey,
  TagOption,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  asSingleSelection,
  createTagOptionInput,
} from '../../../EntitySelection/utils';
import { assertCondition } from '../../../../../utils/types';
import {
  IEntitySelectionProps,
  ISelectedEntity,
} from '../../../EntitySelection/types';
import EntitySelection from '../../../EntitySelection';
import usePermission from '../../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../../pages/PermissionControls/PermissionElementWrapper';
import * as BuilderUI from '../../../BuilderUI';
import KubitTooltip from '../../../../KubitTooltip';
import { SquareAddButton, SquareRemoveButton } from '../../../../Button';

interface IProps {
  rowMeta: IRowMeta;
  onCreateCustom?: IEntitySelectionProps['onCreateCustom'];
  onClose?: () => void;
  onRemove?: () => void;
  showMenuByDefault?: boolean;
  onDeactivateHighlight?: () => void;
}

export default ({
  rowMeta,
  onCreateCustom,
  onClose,
  onRemove,
  showMenuByDefault,
  onDeactivateHighlight,
}: IProps) => {
  const dispatch = useAppDispatch();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const { checkIsPermitted } = usePermission();
  const isPermittedToReadCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortRead],
    schemaId: activeSchemaId,
  });
  const isCohortSamplePage = rowMeta.rowType === RowType.builderCohorts;

  const currentRow: ICohortRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );
  const currentRows: ICohortRow[] = useAppSelector((state) =>
    rowsSelector(state, rowMeta),
  );

  const { cohortId, showCohort, isExpanded, defaultNamedCohort } = currentRow;

  const handleActivate = () => {
    dispatch(setRowPropAction({ rowMeta, row: { showCohort: true } }));
  };

  const handleSelectNamedCohort = (cohort: ISelectedEntity) => {
    assertCondition(
      cohort.__typename === 'RetentionCohort' ||
        cohort.__typename === 'FunnelCohort' ||
        cohort.__typename === 'PerformedCohort' ||
        cohort.__typename === 'CompoundCohort' ||
        cohort.__typename === 'ExpressionCohort' ||
        cohort.__typename === 'MeasureCohort',
    );

    if (rowMeta.rowType === RowType.builderCohorts) {
      batch(() => {
        dispatch(removeErrorAction({ rowMeta, errorKey: '', clearRow: true }));
        dispatch(
          populateNamedBuilderCohort({
            cohort,
            rowMeta,
          }),
        );
      });

      return;
    }

    batch(() => {
      dispatch(removeErrorAction({ rowMeta, errorKey: 'cohortFilter' }));
      // Populate cohort as reference
      dispatch(
        populateNamedCohortReference({
          cohort,
          rowMeta,
        }),
      );
    });
  };

  const handleResetNamedCohort = () => {
    onDeactivateHighlight?.();
    dispatch(resetCohortRow(rowMeta));
  };

  const handleExpand = () => {
    dispatch(setRowPropAction({ rowMeta, row: { isExpanded: true } }));
  };

  const includeCompound = isCohortSamplePage ? currentRows.length === 1 : true;

  const tags = [createTagOptionInput(TagOption.SimpleCohort)];
  if (includeCompound) {
    tags.push(createTagOptionInput(TagOption.CompoundCohort));
  }

  return (
    <PermissionElementWrapper isPermitted={isPermittedToReadCohort}>
      <BuilderUI.BuilderFieldContainer>
        <KubitTooltip
          noOpenDelay={true}
          disabled={!isPermittedToReadCohort}
          content="Apply a previously saved Cohort"
        >
          <BuilderUI.BuilderFieldLabel>
            Saved Cohort
          </BuilderUI.BuilderFieldLabel>
        </KubitTooltip>
        {showCohort || showMenuByDefault ? (
          <>
            <EntitySelection
              autoFocus={!cohortId}
              schemaId={activeSchemaId}
              placeholder="Search and select cohort"
              tags={tags}
              isDisabled={!isPermittedToReadCohort}
              disabledClickHandler={handleResetNamedCohort}
              onSelectEntity={handleSelectNamedCohort}
              onRemoveEntity={handleResetNamedCohort}
              selectedEntities={asSingleSelection(defaultNamedCohort)}
              onCreateCustom={onCreateCustom}
              onClose={onClose}
              labelProps={[
                {
                  type: 'Cohort',
                  onExpand: handleExpand,
                  isExpanded: Boolean(isExpanded),
                },
              ]}
            />
            <SquareRemoveButton
              onClick={onRemove || handleResetNamedCohort}
              content={'Remove Saved Cohort'}
            />
          </>
        ) : (
          <SquareAddButton
            onClick={handleActivate}
            disabled={!isPermittedToReadCohort}
          />
        )}
      </BuilderUI.BuilderFieldContainer>
    </PermissionElementWrapper>
  );
};
